<script setup>
import { computed, ref, watch, nextTick, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const inboxesUiFlags = useMapGetter('inboxes/getUIFlags');
const idealPreventivePrefill = inject('idealPreventivePrefill', null);

const searchQuery = ref('');
const selectedTemplate = ref(null);
const popoverRef = ref(null);
const autoOpenedFromPrefill = ref(false);

// Gates the trigger while ComposeConversation's on-open refetch is in flight,
// so an agent can't open the picker before a stale template list is refreshed.
const isRefreshingTemplates = computed(() => inboxesUiFlags.value.isFetching);

const whatsAppTemplateMessages = computed(() => {
  return getFilteredWhatsAppTemplates.value(props.inboxId);
});

const filteredTemplates = computed(() => {
  return whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const initialBodyParams = computed(() => {
  const prefill = idealPreventivePrefill?.value;
  if (!prefill) return {};

  const body = {};
  if (prefill.name) body['1'] = prefill.name;
  if (prefill.equipment) body['2'] = prefill.equipment;
  if (prefill.installDate) body['3'] = prefill.installDate;
  return body;
});

const getTemplateBody = template => {
  return template.components.find(component => component.type === 'BODY').text;
};

const pickPreferredTemplate = () => {
  const templates = whatsAppTemplateMessages.value || [];
  if (!templates.length) return null;

  const preferredName = idealPreventivePrefill?.value?.template;
  if (preferredName) {
    const exact = templates.find(template => template.name === preferredName);
    if (exact) return exact;
  }

  const preventive = templates.filter(template => {
    const name = String(template.name || '').toLowerCase();
    return (
      name.includes('manutenc') ||
      name.includes('preventiv') ||
      name.includes('aviso')
    );
  });

  return preventive[0] || templates[0] || null;
};

const handlePopoverShow = () => {
  searchQuery.value = '';
  if (!autoOpenedFromPrefill.value) {
    selectedTemplate.value = null;
  }
};

const handlePopoverHide = () => {
  selectedTemplate.value = null;
  autoOpenedFromPrefill.value = false;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
};

const handleBack = () => {
  selectedTemplate.value = null;
};

const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};

watch(
  [() => props.inboxId, whatsAppTemplateMessages, isRefreshingTemplates],
  async () => {
    if (autoOpenedFromPrefill.value) return;
    if (!idealPreventivePrefill?.value) return;
    if (!props.inboxId || isRefreshingTemplates.value) return;

    const template = pickPreferredTemplate();
    if (!template) return;

    autoOpenedFromPrefill.value = true;
    selectedTemplate.value = template;
    await nextTick();
    popoverRef.value?.show?.();
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <Popover
    ref="popoverRef"
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate || isRefreshingTemplates"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div
        v-if="!selectedTemplate"
        class="flex flex-col gap-2 p-4 items-center w-[21.875rem]"
      >
        <div class="relative w-full">
          <Icon
            icon="i-lucide-search"
            class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
          />
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <div
          v-for="template in filteredTemplates"
          :key="template.id"
          class="flex flex-col gap-2 p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
          @click="handleTemplateClick(template)"
        >
          <span class="text-sm text-n-slate-12">{{ template.name }}</span>
          <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
            {{ getTemplateBody(template) }}
          </p>
        </div>
        <template v-if="filteredTemplates.length === 0">
          <p class="pt-2 w-full text-sm text-n-slate-11">
            {{
              t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE')
            }}
          </p>
        </template>
      </div>
      <WhatsappTemplate
        v-else
        :template="selectedTemplate"
        :initial-body-params="initialBodyParams"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>
